import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { hot } from 'react-hot-loader/root';
import cn from 'clsx';
import { isEmpty } from 'lodash';

import Collapsible from '../../library/collapsible/collapsible';
import DynamicMap from '../../library/dynamic-map/dynamic-map';
import Pagination from '../../library/pagination/pagination';
import PaginationMessage from './components/pagination-message/pagination-message';
import PitpassFilter from './components/pitpass-filter/pitpass-filter';
import StoreCountsByLocation from './components/store-counts-by-location/store-counts-by-location';
import FindStore from '../find-store/find-store';
import Message from '../message/message';
import StoreLocatorMessage from '../store-locator-message/store-locator-message';
import StoreQuickView from '../store-quick-view/store-quick-view';
import StoreQuickViewList, {
  LIST_TYPE
} from '../store-quick-view-list/store-quick-view-list';

import {
  GET_STATE_STORE_LIST,
  GET_STORES_BY_CITY_STATE,
  GET_STORES_BY_LAT_LONG,
  GET_STORES_BY_SEARCH_TERM
} from './graphql/store-locator-queries';

import {
  useContainerQuery,
  useLazyQuery,
  useUserLocation
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import {
  getStoreDataLayerValues,
  getStoreDetailsUrl,
  getStoreHours
} from '../../shared/utils/store/store';
import { isArrayWithLength } from '../../shared/utils/validators/validators';
import { getRadiusExpansionInfo } from './utils/store-locator-utils';

import { StoreData, StoreSearchResult } from '../../shared/types/graphql-types';
import { LOCATOR_MARKETING_MESSAGE } from '../../shared/constants/messages';
import { SEARCH_RADIUS_OPTIONS } from '../../shared/constants/search-types';
import {
  SEARCH_BY_CITY_STATE,
  SEARCH_BY_LAT_LONG,
  SEARCH_BY_TERM
} from './constants/store-locator-constants';

import styles from './store-locator.scss';

const INITIAL_SEARCH_RADIUS = SEARCH_RADIUS_OPTIONS[3].value;
const fetchPolicy = 'cache-and-network';

interface MapMarkerPosition {
  latitude?: number;
  longitude?: number;
}

interface StoreLocatorProps {
  className?: string;
  defaultSearchTerm?: string;
  hideReadReviews?: boolean;
  hideStoreDetailsLink?: boolean;
  myStore: StoreData | null;
  myStoreTitle?: string;
  onContinueClick?: () => void;
  onSelectStore?: () => void;
  onSetStoreClick?: (results: object) => void;
  onShareClick?: () => void;
  onSubmit?: () => void;
  selectStoreButtonLabel?: string;
  serviceCodes?: Array<string>;
  shouldSetStoreAndCart?: boolean;
  shouldSummarizePackage?: boolean;
  showAppointmentCTA?: boolean;
  showContinueCTA?: boolean;
  showMakeMyStoreCTA?: boolean;
  showShopProductsCTA?: boolean;
  siteSwapRedirectUrl?: string;
  storeInfoMessage?: string;
}

function StoreLocator(props: StoreLocatorProps) {
  const {
    className = '',
    defaultSearchTerm = '',
    hideReadReviews,
    hideStoreDetailsLink = false,
    myStore,
    myStoreTitle,
    onContinueClick,
    onSelectStore,
    onSetStoreClick,
    onShareClick,
    onSubmit,
    selectStoreButtonLabel,
    serviceCodes,
    shouldSetStoreAndCart,
    shouldSummarizePackage,
    showAppointmentCTA,
    showContinueCTA,
    showMakeMyStoreCTA,
    showShopProductsCTA,
    siteSwapRedirectUrl,
    storeInfoMessage = ''
  } = props;
  const [centerPos, setCenterPos] = useState<MapMarkerPosition>();
  const [selectedStoreIdx, setSelectedStoreIdx] = useState(0);
  const [radius, setRadius] = useState(INITIAL_SEARCH_RADIUS);
  const [searchTerm, setSearchTerm] = useState(defaultSearchTerm);
  const [showLocationsByCity, setShowLocationsByCity] = useState(false);
  const [shouldCheckForRadiusExpansion, setShouldCheckForRadiusExpansion] =
    useState(false);
  const [radiusExpandedMsg, setRadiusExpandedMsg] = useState<string | null>(
    null
  );
  const [latestSearch, setLatestSearch] = useState('');
  const [latestSearchType, setLatestSearchType] = useState(null);
  const [searchResults, setSearchResults] = useState<StoreSearchResult>();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showMapView, setShowMapView] = useState(true);
  const [currentSearchedState, setCurrentSearchedState] = useState('');
  const [storeLocatorDataLayerValues, setStoreLocatorDataLayerValues] =
    useState({});
  const [shouldFilterByPitPass, setShouldFilterByPitPass] = useState(false);

  const { isDenied, userLocation } = useUserLocation();
  const { isMinLg } = useContainerQuery();

  const {
    data: storesBySearchTermData,
    error: storesBySearchTermError,
    fetch: searchStoresByTerm,
    isLoading: storesBySearchLoading
  } = useLazyQuery(GET_STORES_BY_SEARCH_TERM, {
    fetchPolicy,
    variables: {
      includeInstallers: false,
      serviceCodes
    }
  });

  const {
    data: stateData,
    error: stateError,
    fetch: getStoreStateList
  } = useLazyQuery(GET_STATE_STORE_LIST, {
    fetchPolicy,
    variables: {
      includeInstallers: false
    }
  });

  const {
    data: storesByCityStateData,
    error: storesByCityStateError,
    fetch: searchStoresByCityState
  } = useLazyQuery(GET_STORES_BY_CITY_STATE, {
    fetchPolicy,
    variables: {
      userStoreCode: myStore?.code || ''
    }
  });

  const {
    data: storesByLatLongData,
    error: storesByLatLongError,
    fetch: searchStoresByLatLong
  } = useLazyQuery(GET_STORES_BY_LAT_LONG, {
    fetchPolicy,
    variables: {
      serviceCodes,
      userStoreCode: myStore?.code
    }
  });

  const storesByLatLong = storesByLatLongData?.store?.listByLatLong;
  const storeLocations = stateData?.store?.countByLocation || [];
  const {
    hasPitPassStore,
    pagination,
    results: stores,
    sourceLatitude,
    sourceLongitude
  } = searchResults || {};
  const myStoreCode = myStore?.code;
  const myStoreLat = myStore?.geoPoint?.latitude;
  const myStoreLong = myStore?.geoPoint?.longitude;
  const isSearchByCity =
    (Array.isArray(stores) && stores.length > 0) || currentSearchedState;

  const mapMarkers = useMemo(() => {
    if (!Array.isArray(stores)) {
      return [];
    }
    return stores.map((store, idx) => {
      const { geoPoint, pitPass } = store || {};
      const { latitude, longitude } = geoPoint || {};
      const page = pagination?.currentPage || 0;
      const storeIdx = idx + 1;
      const markerNumber = page > 0 ? page * 10 + storeIdx : storeIdx;
      const isActive = selectedStoreIdx === storeIdx;

      return {
        isActive,
        latitude,
        longitude,
        markerNumber,
        pitPass
      };
    });
  }, [pagination, selectedStoreIdx, stores]);

  const handleInitialLoadTimer = useRef<NodeJS.Timeout>();
  const storeListRef = useRef({});
  const myStoreRef = useRef<HTMLDivElement>(null);
  const prevMyStoreCodeRef = useRef(myStore?.code);

  const appendStoreFilter = useCallback(
    variables => {
      const modifiedVariables = {
        includeInstallers: false,
        page: 0,
        ...variables
      };

      if (shouldFilterByPitPass) {
        modifiedVariables.additionalFilters = {
          key: 'PIT_PASS',
          value: 'true'
        };
      }

      return modifiedVariables;
    },
    [shouldFilterByPitPass]
  );

  const searchStoresBy = useCallback(
    (name, variables) => {
      switch (name) {
        case SEARCH_BY_CITY_STATE: {
          searchStoresByCityState(appendStoreFilter(variables));
          break;
        }
        case SEARCH_BY_LAT_LONG: {
          searchStoresByLatLong(appendStoreFilter(variables));
          break;
        }
        case SEARCH_BY_TERM: {
          searchStoresByTerm(appendStoreFilter(variables));
          break;
        }
      }

      setLatestSearchType(name);
    },
    [
      appendStoreFilter,
      searchStoresByCityState,
      searchStoresByLatLong,
      searchStoresByTerm
    ]
  );

  function onSearchTermChange(e) {
    const { target } = e;
    let searchTerm = target ? target.value : '';

    setLatestSearch('');
    setShouldFilterByPitPass(false);
    setSearchTerm(searchTerm);
  }

  function onRadiusChange(selectedOption) {
    if (selectedOption?.value) {
      setRadius(selectedOption.value);
    }
  }

  const scrollToStore = useCallback(
    storeIdx => {
      if (storeIdx === 'myStore') {
        // setTimeout here ensures focus has a chance to clear from other actions.
        setTimeout(() => {
          myStoreRef?.current?.scrollIntoView?.({
            behavior: 'smooth',
            // nearest is needed to avoid scrolling the window.
            block: 'nearest'
          });
        }, 100);
      } else if (typeof storeIdx === 'number') {
        // setTimeout here ensures focus has a chance to clear from other actions.
        setTimeout(() => {
          storeListRef?.current[storeIdx - 1]?.scrollIntoView?.({
            behavior: 'smooth',
            // 'nearest' is needed to avoid scrolling the window for first and last list items.
            // 'center' can cause the list _AND_ the window to scroll.
            block:
              Object.keys(storeListRef?.current || {}).length === storeIdx ||
              (!myStore && storeIdx === 1)
                ? 'nearest'
                : 'center'
          });
        }, 100);
      }
      setSelectedStoreIdx(typeof storeIdx === 'number' ? storeIdx : 0);
    },
    [myStore]
  );

  const centerMapOnMarkerClick = useCallback(
    storeIdx => {
      if (storeIdx === 'myStore' && myStoreLat && myStoreLong) {
        setCenterPos({
          latitude: myStoreLat,
          longitude: myStoreLong
        });
      } else {
        const newSelectedStoreIdx = typeof storeIdx === 'number' ? storeIdx : 0;
        if (newSelectedStoreIdx > 0) {
          const selectedMarker = mapMarkers[newSelectedStoreIdx - 1];
          if (selectedMarker) {
            setCenterPos({
              latitude: selectedMarker.latitude,
              longitude: selectedMarker.longitude
            });
          }
        }
      }
    },
    [mapMarkers, myStoreLat, myStoreLong]
  );

  const onNearbyStoreClick = useCallback(
    e => {
      scrollToStore(e);
      centerMapOnMarkerClick(e);
    },
    [centerMapOnMarkerClick, scrollToStore]
  );

  const onMyStoreClick = useCallback(() => {
    scrollToStore('myStore');
    centerMapOnMarkerClick('myStore');
  }, [centerMapOnMarkerClick, scrollToStore]);

  function handleSearchByState(name) {
    getStoreStateList({ state: name });

    setCurrentSearchedState(name);
    setLatestSearch(name);
  }

  function handlePagination(page) {
    if (myStore) {
      searchStoresBy(SEARCH_BY_LAT_LONG, {
        latitude: searchResults?.sourceLatitude,
        longitude: searchResults?.sourceLongitude,
        page,
        radius
      });
    }

    scrollToStore(1);
  }

  function handleCollapsibleCitySearch(city) {
    if (city && currentSearchedState) {
      trackAction('store_locator_state_city', storeLocatorDataLayerValues);
      searchStoresBy(SEARCH_BY_CITY_STATE, {
        city,
        state: currentSearchedState
      });

      setLatestSearch(city);
      setShowMapView(true);
      setShowLocationsByCity(false);
      window.scrollTo(0, 0);
    }
  }

  function getStoreListNumber(page) {
    return page > 0 ? parseInt(page + '0') : page;
  }

  function togglePitPassFilter() {
    setShouldFilterByPitPass(
      prevShouldFilterByPitPass => !prevShouldFilterByPitPass
    );
  }

  useEffect(() => {
    function handleInitialPageLoad() {
      setHasInitialized(true);
      if (!myStore) {
        setShowMapView(false);
        getStoreStateList();
      } else {
        searchStoresBy(SEARCH_BY_TERM, {
          radius: INITIAL_SEARCH_RADIUS,
          userStoreCode: myStore?.code
        });
      }
    }

    handleInitialLoadTimer.current = setTimeout(handleInitialPageLoad, 5000);

    return () => {
      clearTimeout(handleInitialLoadTimer.current);
    };
  }, [getStoreStateList, myStore, myStoreLat, myStoreLong, searchStoresBy]);

  useEffect(() => {
    const searchStores = storesBySearchTermData?.store || {};
    if (
      searchStores?.search &&
      hasInitialized &&
      latestSearchType === SEARCH_BY_TERM
    ) {
      setSearchResults(searchStores.search);
    }
  }, [hasInitialized, latestSearchType, storesBySearchTermData?.store]);

  useEffect(() => {
    const searchStoresByCity = storesByCityStateData?.store?.searchByCity || {};
    if (
      (searchStoresByCity?.pagination || searchStoresByCity?.results) &&
      latestSearchType === SEARCH_BY_CITY_STATE
    ) {
      setSearchResults(searchStoresByCity);
    }
  }, [latestSearchType, storesByCityStateData?.store?.searchByCity]);

  useEffect(() => {
    if (
      (storesByLatLong?.pagination || storesByLatLong?.results) &&
      latestSearchType === SEARCH_BY_LAT_LONG
    ) {
      setSearchResults(storesByLatLong);
    }
  }, [latestSearchType, storesByLatLong]);

  useEffect(() => {
    if (stores && hasInitialized) {
      let firstNearbyStoreState;
      if (Array.isArray(stores) && stores.length > 0) {
        firstNearbyStoreState = stores[0]?.address?.region;
      }

      if (firstNearbyStoreState) {
        setCurrentSearchedState(firstNearbyStoreState.name);
        getStoreStateList({
          state: showMapView ? firstNearbyStoreState.name : ''
        });
      } else {
        setCurrentSearchedState('');
        getStoreStateList();
      }
    }
  }, [stores, hasInitialized, getStoreStateList, showMapView]);

  useEffect(() => {
    if (myStore) {
      const variables = {
        radius: INITIAL_SEARCH_RADIUS,
        searchTerm: myStore?.address?.postalCode,
        userStoreCode: myStore?.code
      };

      searchStoresBy(SEARCH_BY_TERM, variables);
      setShowMapView(true);
      setHasInitialized(true);
      clearTimeout(handleInitialLoadTimer.current);

      if (
        prevMyStoreCodeRef.current &&
        prevMyStoreCodeRef.current !== myStore.code
      ) {
        prevMyStoreCodeRef.current = myStore.code;
        scrollToStore('myStore');
      }
    }
  }, [myStore, searchStoresBy, scrollToStore]);

  useEffect(() => {
    let variables, search;

    if (latestSearch) {
      search = SEARCH_BY_TERM;
      variables = {
        radius,
        searchTerm: latestSearch,
        userStoreCode: myStoreCode
      };
    } else {
      search = SEARCH_BY_LAT_LONG;

      if (myStoreLat && myStoreLong) {
        variables = {
          latitude: myStoreLat,
          longitude: myStoreLong,
          radius: INITIAL_SEARCH_RADIUS
        };
      } else if (!isDenied && userLocation) {
        variables = {
          ...userLocation,
          radius: INITIAL_SEARCH_RADIUS
        };
        setShowMapView(true);
      }
    }

    if (variables && search) {
      searchStoresBy(search, variables);
    }
  }, [
    isDenied,
    latestSearch,
    myStoreCode,
    myStoreLat,
    myStoreLong,
    radius,
    searchStoresBy,
    userLocation
  ]);

  useEffect(() => {
    setStoreLocatorDataLayerValues(getStoreDataLayerValues(myStore));
  }, [myStore]);

  useEffect(() => {
    if (
      latestSearchType === SEARCH_BY_TERM &&
      storesBySearchTermData &&
      shouldCheckForRadiusExpansion
    ) {
      setShouldCheckForRadiusExpansion(false);
      const { msg, newRadius } = getRadiusExpansionInfo(
        radius,
        storesBySearchTermData
      );
      setRadiusExpandedMsg(msg);
      setRadius(newRadius);
    }
  }, [
    latestSearchType,
    radius,
    shouldCheckForRadiusExpansion,
    storesBySearchTermData
  ]);

  useEffect(() => {
    if (sourceLatitude && sourceLongitude) {
      setCenterPos({
        latitude: sourceLatitude || 33.6805,
        longitude: sourceLongitude || -111.9771
      });
    }
  }, [sourceLatitude, sourceLongitude]);

  if (
    (storesBySearchTermError ||
      storesByCityStateError ||
      storesByLatLongError ||
      stateError) &&
    !window.isPreRendering
  ) {
    throw (
      storesBySearchTermError ||
      storesByCityStateError ||
      storesByLatLongError ||
      stateError
    );
  }

  const storeQuickViews = stores?.map(store => ({
    additionalServices: store?.additionalServices,
    address: store?.address,
    distance: store?.distance,
    geoPoint: store?.geoPoint,
    legacyStoreCode: store?.legacyStoreCode,
    onChangeStore: onSubmit,
    pitPass: store?.pitPass,
    rating: store?.rating,
    shouldSummarizePackage: shouldSummarizePackage,
    showAltDirectionsCTA: true,
    showAltScheduleAppointmentCTA: true,
    showAltShareStoreCTA: true,
    showMakeMyStoreCTA: true,
    storeCode: store?.code,
    storeDetailsLink: hideStoreDetailsLink
      ? null
      : getStoreDetailsUrl(store?.address, store?.code),
    storeHours: getStoreHours()
  }));

  return (
    <div className={cn(styles['store-locator'], 'store-locator')}>
      {storeInfoMessage && isMinLg && (
        <Message
          data-testid="store-locator__message"
          isSolidInfo
          message={storeInfoMessage}
        />
      )}
      <div
        className={cn(
          showMapView
            ? `${styles['container']} ${
                storeInfoMessage
                  ? styles['container--store-locator-message']
                  : ''
              }`
            : styles['container-no-map'],
          className
        )}
      >
        <div
          className={cn(
            styles['store-search'],
            showMapView && styles['search-grid']
          )}
        >
          <FindStore
            isSearching={storesBySearchLoading}
            onRadiusChange={onRadiusChange}
            onSearchTermChange={onSearchTermChange}
            onSubmit={e => {
              e.preventDefault();
              trackAction('store_locator_search', {
                store_search_radius: radius,
                store_search_term: searchTerm
              });

              setLatestSearch(searchTerm);
              setShouldFilterByPitPass(false);
              setShouldCheckForRadiusExpansion(true);
              setRadiusExpandedMsg(null);
              setShowMapView(true);
            }}
            radius={radius}
            searchTerm={searchTerm}
          />
        </div>
        {storeInfoMessage && !isMinLg && (
          <div className={styles['store-locator-message']}>
            <Message
              data-testid="store-locator__message"
              isSolidInfo
              message={storeInfoMessage}
            />
          </div>
        )}
        {showMapView && (
          <>
            <div
              className={cn(styles['map'], 'store-locator__map')}
              data-testid="store-locator__map"
            >
              {!window.isPreRendering && centerPos && (
                <DynamicMap
                  apiKey={process.env.__GOOGLE_API_KEY__}
                  centerPos={centerPos}
                  markers={(Array.isArray(stores) && mapMarkers) || null}
                  myStorePos={myStore?.geoPoint}
                  onMyStoreClick={onMyStoreClick}
                  onNearbyStoreClick={onNearbyStoreClick}
                  userPos={userLocation}
                />
              )}
            </div>
            <div
              className={cn(styles['store-list'], 'store-locator__store-list')}
            >
              {myStore && (
                <div
                  className={
                    selectedStoreIdx === 0
                      ? styles['my-store-selected']
                      : styles['my-store']
                  }
                  ref={myStoreRef}
                >
                  <StoreQuickView
                    address={
                      window.isPreRendering ? undefined : myStore?.address
                    }
                    className="my-store"
                    distance={window.isPreRendering ? 0 : myStore?.distance}
                    geoPoint={
                      window.isPreRendering ? undefined : myStore?.geoPoint
                    }
                    hideReadReviews={hideStoreDetailsLink}
                    isCollapsible
                    isMyStore
                    isPitPassStore={myStore?.pitPass}
                    legacyStoreCode={myStore?.legacyStoreCode || ''}
                    myStoreTitle={myStoreTitle}
                    onAddressClick={onMyStoreClick}
                    onCallStore={() =>
                      trackAction(
                        'store_locator_call_store',
                        storeLocatorDataLayerValues
                      )
                    }
                    onChangeStore={onSubmit}
                    onContinueClick={onContinueClick}
                    onDistanceClick={onMyStoreClick}
                    onGetDirectionsClick={() =>
                      trackAction(
                        'store_locator_directions',
                        storeLocatorDataLayerValues
                      )
                    }
                    onReadReviewClick={() =>
                      trackAction(
                        'store_locator_read_reviews',
                        storeLocatorDataLayerValues
                      )
                    }
                    onScheduleAppointmentClick={() =>
                      trackAction(
                        'store_locator_schedule_appointment',
                        storeLocatorDataLayerValues
                      )
                    }
                    onSelectStore={onSelectStore}
                    onSetStoreClick={() => {
                      trackAction(
                        'store_locator_make_this_my_store',
                        storeLocatorDataLayerValues
                      );
                    }}
                    onShareClick={onShareClick}
                    onShopProductsClick={() =>
                      trackAction(
                        'store_locator_shop_products',
                        storeLocatorDataLayerValues
                      )
                    }
                    onStoreDetailsLinkClick={() =>
                      trackAction(
                        'store_locator_details',
                        storeLocatorDataLayerValues
                      )
                    }
                    rating={window.isPreRendering ? undefined : myStore?.rating}
                    shorterWaitTimeMessageKey={LOCATOR_MARKETING_MESSAGE}
                    shouldSummarizePackage={shouldSummarizePackage}
                    showAltDirectionsCTA
                    showAltShareStoreCTA
                    showAppointmentCTA={showAppointmentCTA}
                    showContinueCTA={showContinueCTA}
                    showMakeMyStoreCTA={showMakeMyStoreCTA}
                    showShopProductsCTA={showShopProductsCTA}
                    siteSwapRedirectUrl={siteSwapRedirectUrl}
                    storeCode={myStore?.code}
                    storeDetailsLink={
                      hideStoreDetailsLink
                        ? undefined
                        : getStoreDetailsUrl(myStore?.address, myStore?.code)
                    }
                    storeHours={getStoreHours()}
                  />
                </div>
              )}

              {isArrayWithLength(stores) && (
                <>
                  {hasPitPassStore && (
                    <PitpassFilter
                      isEnabled={shouldFilterByPitPass}
                      onToggle={togglePitPassFilter}
                    />
                  )}

                  {!isEmpty(pagination) && !window.isPreRendering && (
                    <PaginationMessage
                      className={
                        radiusExpandedMsg
                          ? styles['pagination-no-border']
                          : undefined
                      }
                      maxResultsPerPage={10}
                      pageIndex={pagination!.currentPage}
                      totalNumberOfResults={pagination!.totalNumberOfResults}
                    />
                  )}

                  {radiusExpandedMsg && (
                    <Message isWarning message={radiusExpandedMsg} />
                  )}

                  <StoreQuickViewList
                    className="store"
                    hideReadReviews={hideReadReviews}
                    listNumberOffset={getStoreListNumber(
                      pagination?.currentPage || 0
                    )}
                    listType={LIST_TYPE.COLLAPSIBLE}
                    onCallStore={dataLayerValues =>
                      trackAction('store_locator_call_store', dataLayerValues)
                    }
                    onGetDirectionsClick={dataLayerValues =>
                      trackAction('store_locator_directions', dataLayerValues)
                    }
                    onNearbyStoreClick={onNearbyStoreClick}
                    onReadReviewClick={dataLayerValues =>
                      trackAction('store_locator_read_reviews', dataLayerValues)
                    }
                    onScheduleAppointmentClick={dataLayerValues =>
                      trackAction(
                        'store_locator_schedule_appointment',
                        dataLayerValues
                      )
                    }
                    onSelectStore={onSelectStore}
                    onSetStoreClick={dataLayerValues => {
                      onSetStoreClick?.(dataLayerValues);
                      trackAction(
                        'store_locator_make_this_my_store',
                        dataLayerValues
                      );
                      trackAction(
                        'store_locator_distance_between_stores',
                        dataLayerValues
                      );
                    }}
                    onShopProductsClick={dataLayerValues =>
                      trackAction(
                        'store_locator_shop_products',
                        dataLayerValues
                      )
                    }
                    onStoreDetailsLinkClick={dataLayerValues =>
                      trackAction(`store_locator_details`, dataLayerValues)
                    }
                    selectStoreButtonLabel={selectStoreButtonLabel}
                    selectedStoreIdx={selectedStoreIdx}
                    shouldSetStoreAndCart={shouldSetStoreAndCart}
                    showAdditionalServicesBanner
                    showAppointmentCTA={showAppointmentCTA}
                    siteSwapRedirectUrl={siteSwapRedirectUrl}
                    storeRef={storeListRef}
                    stores={window.isPreRendering ? [] : storeQuickViews}
                  />

                  {!window.isPreRendering &&
                    pagination?.totalNumberOfResults > 10 && (
                      <div className={styles['pagination']}>
                        <Pagination
                          currentPage={pagination?.currentPage}
                          numberOfPages={pagination?.numberOfPages}
                          onPageChange={e => handlePagination(e)}
                        />
                      </div>
                    )}
                </>
              )}
              {!storesBySearchLoading && !isArrayWithLength(stores) && (
                <StoreLocatorMessage
                  isValidSearch={Boolean(stores)}
                  latestSearch={latestSearch}
                  searchRadius={radius}
                />
              )}
            </div>
          </>
        )}
      </div>

      <section className={styles['locations']}>
        {showMapView && (
          <Collapsible
            collapseType="SECONDARY"
            isOpen={showLocationsByCity}
            label={`Tire and Wheel Stores Located by ${
              currentSearchedState ? 'City' : 'State'
            }`}
            onToggle={() => {
              setShowLocationsByCity(!showLocationsByCity);
            }}
          >
            <h4 className={styles['locations-heading']}>
              {currentSearchedState} Tire and Wheel Stores
            </h4>
            <StoreCountsByLocation
              locations={storeLocations}
              onClick={
                isSearchByCity
                  ? e => handleCollapsibleCitySearch(e)
                  : e => handleSearchByState(e)
              }
            />
          </Collapsible>
        )}
        {!showMapView && (
          <>
            <h3 className={styles['locations-title']}>Discount Tire Stores</h3>
            <h4 className={styles['locations-heading']}>{`Stores Located by ${
              currentSearchedState ? 'City' : 'State'
            }`}</h4>
            <StoreCountsByLocation
              locations={storeLocations}
              onClick={
                isSearchByCity
                  ? e => handleCollapsibleCitySearch(e)
                  : e => handleSearchByState(e)
              }
            />
          </>
        )}
      </section>
    </div>
  );
}

export default hot(StoreLocator);
